import { useState, useEffect } from "react";
import styles from "./MyLoans.module.scss";
import AfterOnboardAPI from "../../services/AfterOnboardAPI";
import MainHeader from "../../components/Header/MainHeader";
import {
  handleShowContextualLoader,
  handlehideContextualLoader,
} from "../../utils/contextualLoaderTrigger";
import LoanDocuments from "./components/LoanDocuments";

export default function MyLoans() {
  const [activeTab, setActiveTab] = useState("All Loans");
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loanStatuses = ["All Loans", "Closed", "Settlement", "Disbursed", "Part Payment"];
  const afterOnboardAPI = new AfterOnboardAPI();

  useEffect(() => {
    fetchLoansData();
  }, []);

  useEffect(() => {
    if (loading) {
      handleShowContextualLoader("Please wait...");
      return;
    } else {
      handlehideContextualLoader();
    }
    return () => {
      handlehideContextualLoader();
    };
  }, [loading]);

  const fetchLoansData = async () => {
    try {
      setLoading(true);
      const response = await afterOnboardAPI.getMyLoansAPI();

      if (response && response.success && response.data) {
        const transformedLoans = response.data.map((loan) => ({
          id: loan.loanNo,
          leadID: loan.leadID,
          customerID: loan.customerID,
          amount: loan.disbursalAmount,
          disbursementDate: loan.disbursalDate,
          repaymentDate: loan.repayDate,
          status: loan.status,
          loanStatus: loan.loanStatus,
          type: loan.loanType,
          tenure: loan.tenure,
          loanAmtApproved: loan.loanAmtApproved,
          adminFee: loan.adminFee,
          GstOfAdminFee: loan.GstOfAdminFee,
          roi: loan.roi,
          restructure_eligible: loan.restructure_eligible,
          documents: loan.documents,
        }));
        setLoans(transformedLoans);
      }
    } catch (error) {
      console.error("Error fetching loans:", error);
      setError("Failed to load loans data");
    } finally {
      setLoading(false);
    }
  };

  const handleActionClick = async (loan, type) => {
    try {
      switch (type) {
        case "Sanction":
          const pdfSanctionResponse = await afterOnboardAPI.getSanctionPdfAPI(
            loan.leadID,
            0,
            "Sanction",
          );
          if (
            pdfSanctionResponse &&
            pdfSanctionResponse.success &&
            pdfSanctionResponse.data.signUrl
          ) {
            window.open(pdfSanctionResponse.data.signUrl, "_blank");
          } else {
            console.log("Unable to load Sanction Letter. Please try again later.");
          }
          break;

        case "Noc":
          const nocButton = document.querySelector(
            `[data-loan-id="${loan.id}"][data-action="noc"]`,
          );
          if (nocButton) {
            nocButton.disabled = true;
            nocButton.textContent = "Downloading...";
          }

          const pdfNOCResponse = await afterOnboardAPI.getSanctionPdfAPI(loan.leadID, 0, "Noc");

          if (pdfNOCResponse && pdfNOCResponse.success && pdfNOCResponse.data.signUrl) {
            window.open(pdfNOCResponse.data.signUrl, "_blank");
          } else {
            console.log("NOC document is not available yet. Please contact support.");
          }

          if (nocButton) {
            nocButton.disabled = false;
            nocButton.textContent = "Download NOC";
          }
          break;

        case "Activity":
          // Add activity log functionality here
          console.log("Activity log for loan:", loan.id);
          break;

        default:
          console.log("Unknown action:", type);
      }
    } catch (error) {
      console.error("Error handling action:", error);
      console.log("An error occurred. Please try again later.");

      if (type === "Noc") {
        const nocButton = document.querySelector(`[data-loan-id="${loan.id}"][data-action="noc"]`);
        if (nocButton) {
          nocButton.disabled = false;
          nocButton.textContent = "Download NOC";
        }
      }
    }
  };

  const filteredLoans =
    activeTab === "All Loans" ? loans : loans.filter((loan) => loan.status === activeTab);

  if (loading) {
    return null;
  }

  return (
    <div className="wrapper auto-height">
      <div className="vertical-column-layout">
        <div className="vertical-column-top">
          <MainHeader backPageName={"/profile"} />
          <div className={styles.myLoanHeader}>
            <div className={styles.myHeader}>
              <h1 className={styles.pageTitle}>My Loans</h1>
              <div className="form-group mt-1">
                <select
                  className="form-select"
                  name="loanStatusFilter"
                  value={activeTab}
                  style={{ width: "115%" }}
                  onChange={(e) => setActiveTab(e.target.value)}
                >
                  {loanStatuses.map((status) => (
                    <option key={status} value={status}>
                      {status}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div className={styles.loansContainer}>
            {filteredLoans?.length === 0 ? (
              <div className={styles.noLoansMessage}>No loans found for the selected status.</div>
            ) : (
              filteredLoans.map((loan) => (
                <div key={loan.id} className={styles.loanCard}>
                  <div className={styles.loanHeader}>
                    <h2>{loan.id}</h2>
                    <span className={`${styles.statusBadge} ${getStatusBadgeClass(loan.status)}`}>
                      {loan.status}
                    </span>
                  </div>

                  <div className={styles.loanMainInfo}>
                    <div className={styles.loanInfoItem}>
                      <span className={styles.infoLabel}>Amount</span>
                      <span className={styles.infoValue}>
                        ₹{loan.amount.toLocaleString("en-IN")}
                      </span>
                    </div>
                    <div className={styles.loanInfoItem}>
                      <span className={styles.infoLabel}>Loan Type</span>
                      <span className={styles.infoValue}>{loan.type}</span>
                    </div>
                  </div>

                  <div className={styles.loanDatesSection}>
                    <div className={styles.loanInfoItem}>
                      <span className={styles.infoLabel}>Loan Disbursement Date</span>
                      <span className={styles.infoValue}>{loan.disbursementDate}</span>
                    </div>
                    <div className={styles.loanInfoItem}>
                      <span className={styles.infoLabel}>Repayment Date</span>
                      <span className={styles.infoValue}>{loan.repaymentDate}</span>
                    </div>
                  </div>

                  <LoanDocuments data={loan.documents} />

                  {/* <div className={styles.loanActions}>
                    <button
                      className={styles.actionLink}
                      onClick={() => handleActionClick(loan, "Sanction")}
                    >
                      View Sanction Letter
                    </button>

                    {loan.status === "Closed" && (
                      <button
                        className={`${styles.actionLink} ${styles.nocButton}`}
                        onClick={() => handleActionClick(loan, "Noc")}
                        data-loan-id={loan.id}
                        data-action="noc"
                        title="Download No Objection Certificate"
                      >
                        Download NOC
                      </button>
                    )}
                  </div> */}
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );

  function getStatusBadgeClass(status) {
    switch (status) {
      case "Active":
      case "Disbursed":
        return styles.activeBadge;
      case "Closed":
        return styles.closedBadge;
      case "Part Payment":
        return styles.partPaymentBadge;
      default:
        return styles.defaultBadge;
    }
  }
}
